/**
 * Get available quarters from pageData directly
 */
export const getAvailableQuartersFromPage = (pageData: any) => {
  const quarters = new Map<string, { quarter: string; version: string; data: any }>();

  if (pageData?.scrapes && pageData.scrapes.length > 0) {
    pageData.scrapes.forEach((scrape: any) => {
      if (scrape.quarter && scrape.version) {
        // Use quarter as key to avoid duplicates (one per quarter)
        quarters.set(scrape.quarter, {
          quarter: scrape.quarter,
          version: scrape.version,
          data: scrape
        });
      }
    });
  }

  // Convert to array and sort by quarter (newest first)
  const sortedQuarters = Array.from(quarters.values()).sort((a, b) => {
    // Sort by quarter string (should naturally sort correctly for YYYY-QX format)
    return b.quarter.localeCompare(a.quarter);
  });
  
  return sortedQuarters;
};

/**
 * Get available quarters from company data for a specific page
 */
export const getAvailableQuarters = (company: any, pageId?: string) => {
  const quarters = new Map<string, { quarter: string; version: string; data: any }>();

  if (company?.pages && company.pages.length > 0) {
    for (const page of company.pages) {
      // If pageId is provided, only get quarters from the matching page
      if (pageId && page.id !== pageId) {
        continue;
      }
      
      if (page.scrapes && page.scrapes.length > 0) {
        page.scrapes.forEach((scrape: any) => {
          if (scrape.quarter && scrape.version) {
            // Use quarter as key to avoid duplicates (one per quarter)
            quarters.set(scrape.quarter, {
              quarter: scrape.quarter,
              version: scrape.version,
              data: scrape
            });
          }
        });
      }
    }
  }

  // Convert to array and sort by quarter (newest first)
  const sortedQuarters = Array.from(quarters.values()).sort((a, b) => {
    // Sort by quarter string (should naturally sort correctly for YYYY-QX format)
    return b.quarter.localeCompare(a.quarter);
  });
  
  return sortedQuarters;
};

/**
 * Get screenshot data for a specific quarter from pageData directly
 */
export const getScreenshotDataForQuarterFromPage = (pageData: any, quarter: string) => {
  if (quarter && pageData?.scrapes && pageData.scrapes.length > 0) {
    // Find the scrape that matches the selected quarter
    const quarterScrape = pageData.scrapes.find((scrape: any) => scrape.quarter === quarter);
    if (quarterScrape && quarterScrape.screenshot_url) {
      return {
        screenshotUrl: quarterScrape.cloudinary_url || quarterScrape.screenshot_url,
        scrape: quarterScrape,
        page: pageData
      };
    }
  }

  return { screenshotUrl: null, scrape: null, page: null };
};

/**
 * Get screenshot data for a specific quarter and page
 */
export const getScreenshotDataForQuarter = (company: any, quarter: string, pageId?: string) => {
  if (quarter && company?.pages && company.pages.length > 0) {
    // Search through pages for the specific quarter
    for (const page of company.pages) {
      // If pageId is provided, only search in the matching page
      if (pageId && page.id !== pageId) {
        continue;
      }
      
      if (page.scrapes && page.scrapes.length > 0) {
        // Find the scrape that matches the selected quarter
        const quarterScrape = page.scrapes.find((scrape: any) => scrape.quarter === quarter);
        if (quarterScrape && quarterScrape.screenshot_url) {
          return {
            screenshotUrl: quarterScrape.cloudinary_url || quarterScrape.screenshot_url,
            scrape: quarterScrape,
            page: page
          };
        }
      }
    }
  }

  return { screenshotUrl: null, scrape: null, page: null };
};

/**
 * Format version for pill display
 */
export const formatVersionForPill = (version: string): string => {
  if (!version) return '';
  return version.length > 10 ? version.substring(0, 10) + '...' : version;
};

/**
 * Format quarter for display
 */
export const formatQuarterDisplay = (quarter: string, version?: string): string => {
  if (!quarter) return '';
  if (version) {
    return `${quarter} (${formatVersionForPill(version)})`;
  }
  return quarter;
};